import AddIcon from '@mui/icons-material/Add';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { FilterForm } from './add-gmail-filter-dialog';
import { useMemo, useState } from 'react';
import AddGmailLabelDialog from '../labels/add-label';
import { LabelModel, MailIdModel } from '../../../../../../types';
import {
  isAbleToSeeUIElement,
  UserRoles,
} from '../../../../../utils/roles/client-roles';
import { useQuery } from '@tanstack/react-query';
import {
  betaListForwardingAddresses,
  UserKeys,
} from '../../../../../api/users-api';
import { Messages } from '../../../../../utils/message-constants';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Grow from '@mui/material/Grow';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Label from '@mui/icons-material/Label';

type FilterFormErrors = FieldErrors<FilterForm> & {
  step2Action?: {
    message?: string;
    type: string;
  };
  step1Criteria?: {
    message?: string;
    type: string;
  };
};

interface AddGmailFilterStepTwoProps {
  control: Control<FilterForm>;
  clearErrors: UseFormClearErrors<FilterForm>;
  getValues: UseFormGetValues<FilterForm>;
  setValue: UseFormSetValue<FilterForm>;
  watch: UseFormWatch<FilterForm>;
  userEmail: string;
  userRoles: string[];
  changeLogRoles: string[];
  sortedGmailLabels: LabelModel[];
  errors: FilterFormErrors;
  userMails: MailIdModel[] | undefined;
  isFetchingUserMails: boolean;
}

export default function AddGmailFilterStepTwo({
  control,
  clearErrors,
  setValue,
  watch,
  userRoles,
  userEmail,
  changeLogRoles,
  sortedGmailLabels,
  errors,
  userMails,
  isFetchingUserMails,
}: AddGmailFilterStepTwoProps) {
  const [openAddLabel, setOpenAddLabel] = useState(false);
  const [selectedLabelId, setSelectedLabelId] = useState('');
  const matchingConversations = useMemo(() => {
    if (!userMails) return 0;
    return new Set(userMails.map((mail) => mail.threadId)).size;
  }, [userMails]);

  const categoryLabels = [
    { id: 'CATEGORY_PERSONAL', name: 'Primary' },
    { id: 'CATEGORY_SOCIAL', name: 'Social' },
    { id: 'CATEGORY_UPDATES', name: 'Updates' },
    { id: 'CATEGORY_FORUMS', name: 'Forums' },
    { id: 'CATEGORY_PROMOTIONS', name: 'Promotions' },
  ];
  const typedErrors = errors as FilterFormErrors;

  const { data: forwardingEmails, isLoading } = useQuery({
    queryKey: [UserKeys.ForwardingAddresses, userEmail],
    queryFn: () => betaListForwardingAddresses(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading(
        "the user's forwarding addresses"
      ),
    },
  });

  return (
    <Stack sx={{ px: 1.5 }}>
      <Typography>
        When a message is an exact match for your search criteria:
      </Typography>

      <Stack
        alignItems="start"
        gap={0.5}
        sx={{ overflowY: 'auto', maxHeight: '400px', px: 1.5 }}
      >
        <Controller
          name={'archive'}
          control={control}
          render={({ field }) => {
            return (
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      if (e.target.checked) {
                        setValue('delete', false);
                      }
                    }}
                    disabled={watch('delete')}
                    checked={field.value}
                  />
                }
                label="Skip the Inbox (Archive it)"
              />
            );
          }}
        />
        <Controller
          name={'markRead'}
          control={control}
          render={({ field }) => {
            return (
              <FormControlLabel
                control={
                  <Checkbox
                    slotProps={{
                      input: {
                        id: 'removeLabelIdsUnread',
                      },
                    }}
                    onChange={(e) => field.onChange(e.target.checked)}
                    checked={field.value}
                  />
                }
                label="Mark as read"
              />
            );
          }}
        />
        <Controller
          name={'star'}
          control={control}
          render={({ field }) => {
            return (
              <FormControlLabel
                control={
                  <Checkbox
                    slotProps={{
                      input: {
                        id: 'addLabelIdsStarred',
                      },
                    }}
                    onChange={(e) => field.onChange(e.target.checked)}
                    checked={field.value}
                  />
                }
                label="Star it"
              />
            );
          }}
        />
        <Stack flexDirection="row" gap={1} width={'100%'}>
          <Controller
            name={'hasLabel'}
            control={control}
            render={({ field }) => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      slotProps={{
                        input: {
                          id: 'addLabelIdsStarred',
                        },
                      }}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        clearErrors('applyLabel');
                      }}
                      checked={field.value}
                    />
                  }
                  label="Apply the label:"
                  sx={{ width: 225 }}
                />
              );
            }}
          />
          <Controller
            name={'applyLabel'}
            control={control}
            render={({ field }) => {
              const addLabelOption: LabelModel = {
                id: 'ADD_NEW_LABEL',
                name: 'Create New Label',
                type: 'user',
                messagesTotal: 0,
                messagesUnread: 0,
                threadsTotal: 0,
                threadsUnread: 0,
                messageVisibility: 'show',
                labelVisibility: 'labelShow',
                color: null,
              };

              const sortedOptions = [...sortedGmailLabels].sort((a, b) =>
                a.name.localeCompare(b.name)
              );
              const options = isAbleToSeeUIElement(
                userRoles,
                UserRoles.AddGmailLabel.value
              )
                ? [addLabelOption, ...sortedOptions]
                : sortedOptions;

              return (
                <Autocomplete
                  autoHighlight
                  autoComplete
                  options={options}
                  renderOption={(props, option) => {
                    if (option.id === 'ADD_NEW_LABEL') {
                      return (
                        <>
                          <li {...props}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <ListItemIcon>
                                <AddIcon color="primary" />
                              </ListItemIcon>
                              <ListItemText color="primary">
                                {option.name}
                              </ListItemText>
                            </Box>
                          </li>
                          <Divider component="li" />
                        </>
                      );
                    }
                    return (
                      <li {...props}>
                        <ListItemIcon>
                          <Label
                            fontSize="small"
                            sx={{
                              color: option.color?.backgroundColor,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText>{option.name}</ListItemText>
                      </li>
                    );
                  }}
                  getOptionLabel={(option) => option.name}
                  disabled={!watch('hasLabel')}
                  value={
                    options.find(
                      (o) => o.id === (selectedLabelId || field.value)
                    ) || null
                  }
                  onChange={(_, newValue) => {
                    if (newValue?.id === 'ADD_NEW_LABEL') {
                      setOpenAddLabel(true);
                    } else {
                      field.onChange(newValue?.id || '');
                      setSelectedLabelId('');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Label"
                      fullWidth
                      autoComplete="off"
                      error={!!errors.applyLabel}
                    />
                  )}
                  slotProps={{
                    listbox: {
                      sx: { maxHeight: 250 },
                    },
                  }}
                  sx={{ width: '100%' }}
                />
              );
            }}
          />
        </Stack>
        <Stack flexDirection="row" gap={1} width={'100%'}>
          <Controller
            name={'hasForward'}
            control={control}
            render={({ field }) => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      slotProps={{
                        input: {
                          id: 'hasForward',
                        },
                      }}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        clearErrors('forward');
                      }}
                      checked={field.value}
                    />
                  }
                  label="Forward it to:"
                  sx={{ width: 225 }}
                />
              );
            }}
          />
          <Controller
            name={'forward'}
            control={control}
            render={({ field }) => (
              <Autocomplete
                autoHighlight
                autoComplete
                options={forwardingEmails || []}
                getOptionLabel={(option) => option.forwardingEmail}
                disabled={!watch('hasForward')}
                loading={isLoading}
                value={
                  forwardingEmails?.find(
                    (o) => o.forwardingEmail === field.value
                  ) || null
                }
                onChange={(_, newValue) => {
                  field.onChange(newValue?.forwardingEmail || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Forwarding Address"
                    fullWidth
                    autoComplete="off"
                    error={!!errors.forward}
                  />
                )}
                slotProps={{
                  listbox: {
                    sx: { maxHeight: 250 },
                  },
                }}
                sx={{ width: '100%' }}
              />
            )}
          />
        </Stack>

        <Controller
          name={'delete'}
          control={control}
          render={({ field }) => {
            return (
              <FormControlLabel
                control={
                  <Checkbox
                    slotProps={{
                      input: {
                        id: 'addLabelIdsTrash',
                      },
                    }}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      if (e.target.checked) {
                        setValue('archive', false);
                      }
                    }}
                    checked={field.value}
                    disabled={watch('archive')}
                  />
                }
                label="Delete it"
              />
            );
          }}
        />
        <Controller
          name={'neverSpam'}
          control={control}
          render={({ field }) => {
            return (
              <FormControlLabel
                control={
                  <Checkbox
                    slotProps={{
                      input: {
                        id: 'removeLabelIdsSpam',
                      },
                    }}
                    onChange={(e) => field.onChange(e.target.checked)}
                    checked={field.value}
                  />
                }
                label="Never send it to Spam"
              />
            );
          }}
        />
        <Controller
          name={'markImportant'}
          control={control}
          render={({ field }) => {
            return (
              <FormControlLabel
                control={
                  <Checkbox
                    slotProps={{
                      input: {
                        id: 'addLabelIdsImportant',
                      },
                    }}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      if (e.target.checked) {
                        setValue('neverMarkImportant', false);
                      }
                    }}
                    checked={field.value}
                    disabled={watch('neverMarkImportant')}
                  />
                }
                label="Mark it as important"
              />
            );
          }}
        />
        <Controller
          name={'neverMarkImportant'}
          control={control}
          render={({ field }) => {
            return (
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      if (e.target.checked) {
                        setValue('markImportant', false);
                      }
                    }}
                    checked={field.value}
                    disabled={watch('markImportant')}
                  />
                }
                label="Never mark as important"
              />
            );
          }}
        />
        <Stack flexDirection="row" gap={1} width={'100%'}>
          <Controller
            name={'hasCategory'}
            control={control}
            render={({ field }) => {
              return (
                <FormControlLabel
                  control={
                    <Checkbox
                      slotProps={{
                        input: {
                          id: 'addLabelIdsCategory',
                        },
                      }}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        clearErrors('category');
                      }}
                      checked={field.value}
                    />
                  }
                  label="Categorize as:"
                  sx={{ width: 225 }}
                />
              );
            }}
          />
          <Controller
            name={'category'}
            control={control}
            render={({ field }) => (
              <Autocomplete
                autoHighlight
                autoComplete
                options={categoryLabels}
                getOptionLabel={(option) => option.name}
                disabled={!watch('hasCategory')}
                value={categoryLabels.find((o) => o.id === field.value) || null}
                onChange={(_, newValue) => {
                  field.onChange(newValue?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Category"
                    fullWidth
                    autoComplete="off"
                    error={!!errors.category}
                  />
                )}
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props;
                  return (
                    <li key={key} {...otherProps}>
                      {option.name}
                    </li>
                  );
                }}
                slotProps={{
                  listbox: {
                    sx: { maxHeight: 250 },
                  },
                }}
                sx={{ width: '100%' }}
              />
            )}
          />
        </Stack>
        <Controller
          name={'applyToExisting'}
          control={control}
          render={({ field }) => {
            return (
              <FormControlLabel
                sx={{ alignSelf: 'flex-start' }}
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    slotProps={{
                      input: {
                        id: 'applyToExisting',
                      },
                    }}
                  />
                }
                label={
                  <Typography>
                    Also apply filter to{' '}
                    {matchingConversations > 0 && !isFetchingUserMails && (
                      <>
                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                          {matchingConversations}
                        </Box>{' '}
                      </>
                    )}
                    matching conversations.
                  </Typography>
                }
              />
            );
          }}
        />
      </Stack>
      {typedErrors.step2Action && (
        <Box width="100%">
          <Grow in={true}>
            <Alert severity="error">
              {typedErrors.step2Action.message ||
                'Please select at least one option from this section for your filter action.'}
            </Alert>
          </Grow>
        </Box>
      )}
      <AddGmailLabelDialog
        open={openAddLabel}
        handleClose={() => setOpenAddLabel(false)}
        userEmail={userEmail}
        userLabels={sortedGmailLabels}
        changeLogRoles={changeLogRoles}
        setSelectedLabelId={setSelectedLabelId}
        setValue={setValue}
      />
    </Stack>
  );
}
